import { useEffect, useRef, useState } from "react";
import { ImageIcon, Loader2, Save, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadProgressBar } from "@/components/ui/upload-progress-bar";
import {
  useCreateClimateProject, useUpdateClimateProject,
  type ClimateProjectInput,
} from "@/hooks/useClimateProjects";
import type { ClimateProjectRow, ClimateProjectStatus, ClimateProjectType } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass an existing project for edit; omit for create. */
  project?: ClimateProjectRow | null;
}

/** Create / edit dialog for ClimateProject — CEO+ only (API enforces). */
export function ClimateProjectFormDialog({ open, onOpenChange, project }: Props) {
  const isEdit = !!project;
  const create = useCreateClimateProject();
  const update = useUpdateClimateProject();
  const mutation = isEdit ? update : create;

  const [title,        setTitle]        = useState("");
  const [initiative,   setInitiative]   = useState("Green Legacy");
  const [summary,      setSummary]      = useState("");
  const [location,     setLocation]     = useState("");
  const [status,       setStatus]       = useState<ClimateProjectStatus>("active");
  const [projectType,  setProjectType]  = useState<ClimateProjectType>("action");
  const [financeAmt,   setFinanceAmt]   = useState("");
  const [financeCur,   setFinanceCur]   = useState("ETB");
  const [fundingSrc,   setFundingSrc]   = useState("");
  const [startDate,    setStartDate]    = useState("");
  const [endDate,      setEndDate]      = useState("");
  const [projectUrl,   setProjectUrl]   = useState("");
  const [isPublished,  setIsPublished]  = useState(true);
  const [isFeatured,   setIsFeatured]   = useState(false);
  const [image,        setImage]        = useState<File | null>(null);
  const [progress,     setProgress]     = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Re-seed when (re)opening.
  useEffect(() => {
    if (!open) return;
    if (project) {
      setTitle(project.title);
      setInitiative(project.initiative || "Green Legacy");
      setSummary(project.summary || "");
      setLocation(project.location || "");
      setStatus(project.status);
      setProjectType(project.project_type);
      setFinanceAmt(project.finance_amount ?? "");
      setFinanceCur(project.finance_currency || "ETB");
      setFundingSrc(project.funding_source || "");
      setStartDate(project.start_date ?? "");
      setEndDate(project.end_date ?? "");
      setProjectUrl(project.project_url || "");
      setIsPublished(project.is_published);
      setIsFeatured(project.is_featured);
    } else {
      setTitle(""); setInitiative("Green Legacy"); setSummary(""); setLocation("");
      setStatus("active"); setProjectType("action");
      setFinanceAmt(""); setFinanceCur("ETB"); setFundingSrc("");
      setStartDate(""); setEndDate(""); setProjectUrl("");
      setIsPublished(true); setIsFeatured(false);
    }
    setImage(null);
    setProgress(null);
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);

  const canSubmit =
    title.trim().length > 0
    && summary.trim().length > 0
    && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const wantsProgress = !!image;
    if (wantsProgress) setProgress(0);

    const input: ClimateProjectInput = {
      title:           title.trim(),
      initiative:      initiative.trim() || "Green Legacy",
      summary:         summary.trim(),
      location:        location.trim(),
      status,
      project_type:    projectType,
      finance_amount:  financeAmt.trim() || null,
      finance_currency: financeCur.trim() || "ETB",
      funding_source:  fundingSrc.trim(),
      start_date:      startDate || null,
      end_date:        endDate || null,
      project_url:     projectUrl.trim(),
      is_published:    isPublished,
      is_featured:     isFeatured,
      image:           image || undefined,
      onProgress:      wantsProgress ? setProgress : undefined,
    };
    const handlers = {
      onSuccess: () => onOpenChange(false),
      onSettled: () => setProgress(null),
    };
    if (isEdit && project) update.mutate({ id: project.id, input }, handlers);
    else                   create.mutate(input, handlers);
  }

  const err = mutation.error as { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined;
  const errorMsg = formatApiError(err);

  return (
    <Dialog open={open} onOpenChange={(v) => !mutation.isPending && onOpenChange(v)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit “${project?.title}”` : "New climate project"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the project's metadata, cover image, and publish state."
              : "Add a project or action to the climate portfolio. Will be visible to all staff once published."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cp-title">Title <span className="text-destructive">*</span></Label>
              <Input id="cp-title" required maxLength={255}
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Green Legacy 2026 reforestation" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cp-initiative">Initiative</Label>
              <Input id="cp-initiative" maxLength={150}
                value={initiative} onChange={(e) => setInitiative(e.target.value)}
                placeholder="Green Legacy" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cp-type">Type</Label>
              <select id="cp-type"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value as ClimateProjectType)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                <option value="project">Project</option>
                <option value="action">Action</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cp-status">Status</Label>
              <select id="cp-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as ClimateProjectStatus)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                <option value="pipeline">Pipeline</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cp-location">Location</Label>
              <Input id="cp-location" maxLength={200}
                value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Oromia / Nationwide" />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cp-summary">Summary <span className="text-destructive">*</span></Label>
              <Textarea id="cp-summary" rows={3} required maxLength={500}
                value={summary} onChange={(e) => setSummary(e.target.value)}
                placeholder="One short paragraph describing what this project does and why it matters." />
            </div>

            {/* Finance row */}
            <div className="space-y-1.5">
              <Label htmlFor="cp-amount">Finance amount</Label>
              <Input id="cp-amount" type="number" step="any" inputMode="decimal"
                value={financeAmt} onChange={(e) => setFinanceAmt(e.target.value)}
                placeholder="0" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-currency">Currency</Label>
              <Input id="cp-currency" maxLength={10}
                value={financeCur} onChange={(e) => setFinanceCur(e.target.value.toUpperCase())}
                placeholder="ETB / USD / EUR …" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cp-fund">Funding source</Label>
              <Input id="cp-fund" maxLength={255}
                value={fundingSrc} onChange={(e) => setFundingSrc(e.target.value)}
                placeholder="e.g. World Bank, EU Green Deal, GoE budget" />
            </div>

            {/* Date row */}
            <div className="space-y-1.5">
              <Label htmlFor="cp-start">Start date</Label>
              <Input id="cp-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-end">End date</Label>
              <Input id="cp-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cp-url">External project URL (optional)</Label>
              <Input id="cp-url" type="url"
                value={projectUrl} onChange={(e) => setProjectUrl(e.target.value)}
                placeholder="https://…" />
            </div>

            {/* Cover image */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Cover image (optional)</Label>
              {image ? (
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-2.5">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm">{image.name}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setImage(null)} disabled={mutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="mr-2 h-3.5 w-3.5" />Pick a cover photo
                </Button>
              )}
              <input
                ref={fileInputRef} type="file" accept="image/*" hidden
                onChange={(e) => { setImage(e.target.files?.[0] ?? null); e.target.value = ""; }}
              />
              {progress !== null && image && (
                <UploadProgressBar progress={progress} totalBytes={image.size} />
              )}
            </div>

            {/* Toggles */}
            <div className="space-y-2 sm:col-span-2">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-primary"
                />
                <div>
                  <div className="font-medium">Published</div>
                  <div className="text-xs text-muted-foreground">
                    When off, only you (and other CEOs) can see this project.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-primary"
                />
                <div>
                  <div className="font-medium">Featured</div>
                  <div className="text-xs text-muted-foreground">
                    Pin to the top of the projects grid.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost"
              onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isEdit ? "Saving…" : "Creating…"}</>
                : <><Save className="mr-2 h-4 w-4" />{isEdit ? "Save changes" : "Create project"}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatApiError(
  err: { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined,
): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return `${field}: ${msgs[0]}`;
    }
  }
  return "Save failed.";
}
